import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import * as con from "../../GlobalConstants"
import {
  getInitialAuthenticationValues,
  getDefaultAuthenticationValues,
  clearLocalAuthenticationStorage
} from "../../utils/authenticationFunctions"
import { setLocalStorageItem } from "../../utils/localStorageWrapper"
import { getNow } from "../../utils/dateFunctions"
import { getRequestConfig } from "../logic/supportFunctions"
import { createError } from "../../templates/notifications/Notifications"


// =====================================================
// LOGIN
// =====================================================

export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password }, { rejectWithValue }) => {
    try {

      const response = await axios.post(
        con.rest_base_url + con.login_api,
        { username, password },
        { headers: { "Content-Type": "application/json" } }
      )

      const data = response.data

      setLocalStorageItem(con.LOCAL_TOKEN, data[con.TOKEN])
      setLocalStorageItem(con.LOCAL_USER, data[con.USER], con.OBJECT)
      setLocalStorageItem(
        con.LOCAL_LAST_TOKEN_REFRESHED_TIMESTAMP,
        getNow()
      )

      return data

    } catch (error) {

      if (
        error.response?.data?.non_field_errors?.[0] === con.WRONG_CREDENTIALS
      ) {
        return rejectWithValue(con.WRONG_CREDENTIALS)
      }

      return rejectWithValue("SERVER_ERROR")
    }
  }
)

// =====================================================
// LOGOUT
// =====================================================

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { getState }) => {
    const state = getState()
    const config = getRequestConfig()
    const role = state[con.REDUCER_AUTHENTICATION].user?.role

    const url = `${con.rest_base_url}${con.logout_api}?role=${encodeURIComponent(role)}`

    try {
      await axios.get(url, config)
    } catch (error) {
      console.warn("Error while logging out")
    }
    clearLocalAuthenticationStorage()

    return true
  }
)

// =====================================================
// INITIAL STATE
// =====================================================

const initialState = {
  ...getInitialAuthenticationValues(),
}

// =====================================================
// SLICE
// =====================================================

const authSlice = createSlice({
  name: con.REDUCER_AUTHENTICATION,
  initialState,

  reducers: {
    setSurrogateUser: (state, action) => {
      state[con.SURROGATE_USER] = action.payload;
    }

  },

  extraReducers: (builder) => {

    builder

      // ================= LOGIN =================

      .addCase(login.pending, (state) => {
        state.status = con.LOADING
        state.error = null
      })

      .addCase(login.fulfilled, (state, action) => {
        state.status = con.OK
        state[con.TOKEN] = action.payload[con.TOKEN]
        state[con.USER] = action.payload[con.USER]
        state[con.IS_AUTHENTICATED] = true
        state[con.NEEDS_TO_LOGIN_AGAIN] = false
      })

      .addCase(login.rejected, (state, action) => {
        state.status = con.ERROR
        state.error = action.payload
        if (action.payload !== con.WRONG_CREDENTIALS)
          createError(action.payload)
      })

      // ================= LOGOUT =================

      .addCase(logout.fulfilled, () => ({
        ...getDefaultAuthenticationValues(),
        status: "idle",
        error: null
      }))
  }
})


export const {
  setSurrogateUser,
} = authSlice.actions;

export default authSlice.reducer
